import { computed, DOCUMENT, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ICLientData } from '../../model/client.model';
import { getCountriesList } from '../../model/countries';
import { ICurrency } from '../../model/currency.model';
import { Currency, EuropeanCountries, GulfCountries } from '../../model/currency-list';
import { IFundraiser } from '../../model/fundraiser.model';
import { IHost } from '../../model/host.model';
import { ILayoutConfig, IPageNames, IPageOrigins } from '../../model/layout-config.model';
import { ICartProduct, ITypParams } from '../../model/payment.model';
import { IUser } from '../../model/user.model';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class VariablesService {
  private readonly platformId = inject(PLATFORM_ID);
  readonly document = inject(DOCUMENT);

  private readonly actRoute = inject(ActivatedRoute);

  readonly maxDonationAmount = 50000000;
  readonly waitForTickerData = signal<boolean>(false);

  readonly minSipAmounts: any = { INR: 100, USD: 10, GBP: 10, EUR: 10, AED: 20, SGD: 30, SAR: 50, CAD: 10, HKD: 10, QAR: 10, AUD: 10, MYR: 10 } as const;
  readonly defaultSipCause = 'medical';
  readonly leadMailTo = 'leads.ketto@gmail.com';

  //products Kart
  readonly selectedKCart = signal<ICartProduct[] | null>(null); //user selected products
  readonly productKartData = signal<any[] | null>(null); //api data for all the products
  readonly kCartDockData = signal<{ image?: string, title?: string, amount?: number, currency_symbol?: string }>({});

  readonly priorCountries = ['us', 'ae', 'gb', 'in'] as const;
  readonly countryList = getCountriesList(['us', 'ae', 'gb', 'in']);

  readonly matchEligible = signal<'' | 'eligible' | 'uneligible'>('');
  readonly matchDonationAmounts = signal<{ [key: string]: number } | null>(null);

  //all medical causes ids
  readonly ALL_MEDICAL_CAUSES: number[] = [4, 7, 59, 103, 150, 156, 159, 160] as const;

  tyParams?: ITypParams;
  readonly showSkeletonFor = signal({
    slab: false,
  });

  readonly browser = signal<string>('Unknown');
  readonly isIOSDevice = signal<boolean>(isPlatformBrowser(this.platformId) && /iPhone Simulator|iPad Simulator|iPod Simulator|iPad|iPhone|iPod/.test(navigator.userAgent) ? true : false);
  readonly isAppleDevice = signal<boolean>(isPlatformBrowser(this.platformId) && /iPhone Simulator|iPad Simulator|iPod Simulator|iPad|iPhone|iPod|Mac/.test(navigator.userAgent) ? true : false);

  readonly logoWidth = signal('1140px');
  readonly clientLocationData = signal<ICLientData | null>(null);
  readonly currencies: ICurrency[] = Currency;
  readonly supportedCurrency = signal<ICurrency[]>(Currency);
  readonly currency = signal<string>('');
  readonly defaultImg = '/assets/images/fallback.svg';
  readonly defaultBlurImg = '/assets/images/defaultBlurImg.png';
  readonly defaultStoryImg = '/assets/images/default/default-story.png';
  readonly defaultSuccessStoryImg = '/assets/images/default/default-success-story.svg';
  readonly deviceType = signal<string>('');
  readonly domainData = signal<IHost>({} as IHost);
  readonly europeanCountries = EuropeanCountries;
  readonly fundraiser = signal<IFundraiser | undefined>(undefined);
  readonly globalVar = signal<any>(null);
  readonly showDockedBtn = signal<boolean>(false);
  readonly gulfCountries = GulfCountries;
  readonly isBrowser = signal<boolean>(false);
  readonly isDomainLoaded = signal<boolean>(false);
  readonly isDummyEmail = signal<boolean>(false);
  readonly isPermanentLoggedIn = signal<boolean>(false);
  readonly isTempLoggedIn = signal<boolean>(false);
  readonly isMobile = signal<boolean>(false);
  readonly isStoryDataLoading = signal<boolean>(false);
  readonly isToken = signal<boolean>(false);
  readonly authToken = signal<string>('');
  readonly isVariableLogin = signal<boolean>(false);
  readonly numberChange = signal<any>(null);
  readonly pageName = signal<IPageNames>('');
  readonly origin = signal<IPageOrigins>('');
  readonly paymentCurrency = signal<string>('');
  readonly poweredBy = signal('equalall');
  readonly previousPageUrl = signal<string>('');
  readonly userData = signal<IUser | undefined>(undefined);
  readonly isDefaultSipCauseActive = computed(() => this.userData()?.listsubscriptions?.[this.defaultSipCause]?.status_flag === 1 ? true : false);
  readonly isIndian = signal<boolean>(false);
  readonly showDonate = signal<boolean>(false);
  readonly utm_url_obj = signal<any>(null);
  readonly utm_url_string = signal<string>('');
  readonly varLoginData = signal<{ user: any, userdata: any }>({ user: null, userdata: null });
  readonly pageLayoutConfig = signal<ILayoutConfig>({} as ILayoutConfig);
  readonly stringOnlyRegex = /^[a-zA-Z\s]+$/;
  readonly numberOnlyRegex = /^\d+(\.\d+)?$/;
  readonly namePatternRegex = /^[a-z ,.'-]+$/i;
  readonly emailPatternRegex = /^[_a-zA-Z0-9-]+(\.[_a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,4})$/;
  readonly pswPatternRegex = /^(?=.*[a-z])+(?=.*[A-Z])+(?=.*\d).{8,}$/;
  readonly expiryRegex = /^(0[1-9]|1[0-2])\/\d{2}$/;
  readonly userPhoneExt = signal<string>('');
  readonly userPhoneNumber = signal<string>('');
  readonly loginMethod = signal<string>('');
  readonly loginType = signal<'signin' | 'signup' | ''>('');
  readonly dummyEmailExtn = '@dummyequalall.org';
  readonly guestEmailExtn = '@dummyeqall.org';
  readonly isGuestUser = signal<boolean>(false);
  readonly donateMultiPatient = signal<boolean>(false);
  readonly recommendedVpa = signal<string>('');
  readonly sidebarDrawerState = signal<boolean>(false);
  readonly isCustomAmountSelected = signal<boolean>(false);
  readonly eventInfos = {
    contributeInitiate: { info_1: '', info_2: '', info_3: '' },
    contributeCart: { info_1: '', info_2: '', info_3: '' },
    orderCreated: { info_1: '', info_2: '', info_3: '' },
  }
  readonly isExitIntentWithMinimumDonation = signal<boolean>(false)

  readonly ALL_SOCIAL_SHARE_AMOUNT: { [key: string]: number } = {
    INR: 5000,
    USD: 250,
    GBP: 250,
    EUR: 250,
    AED: 250,
    SAR: 250,
    SGD: 100,
    CAD: 500,
    HKD: 500,
    QAR: 500,
    AUD: 500,
    MYR: 50
  } as const;

  readonly bulkDonationHiddenData: { [key: string]: number } = {
    AED: 500,
    AUD: 100,
    EUR: 160,
    CAD: 100,
    USD: 300
  } as const;

  readonly queryParams = toSignal(this.actRoute.queryParams, { initialValue: {} as any });

  readonly defaultOrganiserName = 'Equal Aid for Equal Lives, Inc';

  constructor() {
    this.detectBrowser();
  }

  private async detectBrowser() {
    if (!this.isBrowser) return;
    const nav: any = navigator;
    if (nav.brave && await nav.brave.isBrave()) { this.browser.set('Brave'); return; }
    const ua = navigator.userAgent;
    if (/Edg/i.test(ua)) { this.browser.set('Edge'); return; }
    if (/Firefox/i.test(ua)) { this.browser.set('Firefox'); return; }
    if (/OPR|Opera/i.test(ua)) { this.browser.set('Opera'); return; }
    if (/Safari/i.test(ua) && !/Chrome|CriOS|Edg/i.test(ua)) { this.browser.set('Safari'); return; }
    if (/MSIE|Trident/i.test(ua)) { this.browser.set('IE'); return; }
    if (/Chrome|CriOS/i.test(ua)) { this.browser.set('Chrome'); return; }
  }

}
